import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'

// Import components (we'll create these next)
import Login from '../views/Login.vue'
import HomeDashboard from '../views/HomeDashboard.vue'
import ProductList from '../views/ProductList.vue'
import ProductForm from '../views/ProductForm.vue'
import SaleScanner from '../views/SaleScanner.vue'
import SalesHistory from '../views/SalesHistory.vue'
import BarcodeScanner from '../components/BarcodeScanner.vue'

const routes = [
  {
    path: '/',
    name: 'Login',
    component: Login,
    meta: { 
      requiresGuest: true,
      title: 'Login - Inventory System'
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: HomeDashboard,
    meta: { 
      requiresAuth: true, 
      role: 'OWNER',
      title: 'Dashboard - Inventory System'
    }
  },
  {
    path: '/products',
    name: 'ProductList',
    component: ProductList,
    meta: { 
      requiresAuth: true, 
      role: 'OWNER',
      title: 'Products - Inventory System'
    }
  },
  {
    path: '/products/new',
    name: 'ProductCreate',
    component: ProductForm,
    meta: { 
      requiresAuth: true, 
      role: 'OWNER',
      title: 'Add Product - Inventory System'
    }
  },
  {
    path: '/products/edit/:id',
    name: 'ProductEdit',
    component: ProductForm,
    meta: { 
      requiresAuth: true, 
      role: 'OWNER',
      title: 'Edit Product - Inventory System'
    }
  },
  {
    path: '/scan',
    name: 'SaleScanner',
    component: SaleScanner,
    meta: { 
      requiresAuth: true,
      title: 'Scanner - Inventory System'
    }
  },
  {
    path: '/scanner-test',
    name: 'BarcodeScannerTest',
    component: BarcodeScanner,
    meta: { 
      requiresAuth: true,
      title: 'Scanner Test - Inventory System'
    }
  },
  {
    path: '/history',
    name: 'SalesHistory',
    component: SalesHistory,
    meta: { 
      requiresAuth: true, 
      role: 'OWNER',
      title: 'Sales History - Inventory System'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Enhanced navigation guard with better error handling
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Set page title
  if (to.meta.title) {
    document.title = to.meta.title
  }
  
  // If we have a token but no user data, try to initialize auth
  if (authStore.token && !authStore.user && !authStore.loading) {
    try {
      await authStore.initializeAuth()
    } catch (error) {
      console.error('Failed to initialize auth:', error)
      authStore.logout()
    }
  }
  
  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    // If not authenticated, redirect to login
    if (!authStore.isAuthenticated) {
      console.log('🔒 Route requires authentication, redirecting to login')
      next({
        name: 'Login',
        query: { redirect: to.fullPath } // Save intended destination
      })
      return
    }
    
    // Check role-based access
    if (to.meta.role) {
      const userRole = authStore.user?.role
      const requiredRole = to.meta.role
      
      if (userRole !== requiredRole) {
        console.log(`🚫 Access denied. Required: ${requiredRole}, User: ${userRole}`)
        
        // Redirect based on user role
        if (userRole === 'OWNER') {
          next({ name: 'Dashboard' })
        } else if (userRole === 'CASHIER') {
          next({ name: 'SaleScanner' })
        } else {
          // Unknown role, logout for safety
          authStore.logout()
          next({ name: 'Login' })
        }
        return
      }
    }
    
    console.log(`✅ Access granted to ${to.name} for ${authStore.user?.role}`)
  }
  
  // Check if route requires guest (not authenticated)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    console.log('🔄 Authenticated user accessing guest route, redirecting')
    
    // Handle redirect after login
    const redirectPath = to.query.redirect
    if (redirectPath) {
      next(redirectPath)
      return
    }
    
    // Redirect authenticated users to appropriate dashboard
    if (authStore.user?.role === 'OWNER') {
      next({ name: 'Dashboard' })
    } else if (authStore.user?.role === 'CASHIER') {
      next({ name: 'SaleScanner' })
    } else {
      next({ name: 'SaleScanner' }) // Default fallback
    }
    return
  }
  
  next()
})

// Global error handler for navigation errors
router.onError((error) => {
  console.error('Router error:', error)
})

// After each navigation, log the route change
router.afterEach((to, from) => {
  console.log(`🧭 Navigated from ${from.name || 'unknown'} to ${to.name || 'unknown'}`)
})

export default router 